/**
 * Turning a name into the two letters that stand in for a photograph.
 *
 * There are no avatars in this school and there should not be: nobody is going to upload a
 * portrait of a nine-year-old, and a row of identical grey silhouettes tells the eye nothing.
 * Initials do the one job an avatar does in a list this long — let you find the person you were
 * talking to without reading.
 */
export function initials(fullName: string): string {
  const parts = fullName
    .split(/\s+/u)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length === 0) return '?';

  const first = [...parts[0]!][0] ?? '';
  const second = parts.length > 1 ? ([...parts[parts.length - 1]!][0] ?? '') : '';

  return (first + second).toUpperCase();
}

/**
 * A stable colour per person.
 *
 * Derived from the name rather than stored, so the same child is the same colour on every screen
 * and after every reload — the point of the colour is recognition, and a colour that changes
 * recognises nothing. Hue only: the wheel is even, so no pupil ends up illegibly dark.
 */
export function avatarHue(fullName: string): number {
  let hash = 0;
  for (const ch of fullName) hash = (hash * 31 + ch.codePointAt(0)!) % 360;
  return hash;
}

/**
 * How many calendar days ago something happened.
 *
 * Calendar days, not elapsed hours: a message sent at 23:50 was "yesterday" at 00:10, twenty
 * minutes later, and anything that answers "0 days" there is wrong in the way people notice.
 */
function daysAgo(at: Date, now: Date): number {
  const then = new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  return Math.round((today - then) / 86_400_000);
}

/**
 * When a message arrived, said the way a person would say it.
 *
 * Today is a clock, yesterday is a word, this week is a weekday, and anything older is a date.
 * "14:03" against a message from March is not information, it is a small trap.
 */
export function whenShort(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const days = daysAgo(at, now);

  if (days <= 0) return at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return 'вчера';
  if (days < 7) return at.toLocaleDateString('ru-RU', { weekday: 'short' });

  return at.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** The heading between two days inside a conversation. */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const days = daysAgo(at, now);

  if (days <= 0) return 'Сегодня';
  if (days === 1) return 'Вчера';

  return at.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    ...(at.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/**
 * When somebody was last here, said the way a teacher would say it.
 *
 * "Ни разу не входил" is the answer worth spotting: a child who was given access and never came
 * is the one row in a list of thirty that needs acting on, and it is invisible in any feed of
 * things that happened — because for them, nothing did.
 */
export function presence(
  lastSeenIso: string | null,
  now: Date = new Date(),
): { label: string; state: 'online' | 'recent' | 'away' | 'never' } {
  if (!lastSeenIso) return { label: 'ни разу не входил', state: 'never' };

  const at = new Date(lastSeenIso);
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);

  // The same five minutes the server uses to decide who is here.
  if (minutes < 5) return { label: 'сейчас на сайте', state: 'online' };
  if (minutes < 60) return { label: `был ${minutes} мин назад`, state: 'recent' };

  const days = daysAgo(at, now);
  if (days === 0) {
    return {
      label: `был сегодня в ${at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`,
      state: 'recent',
    };
  }
  if (days === 1) {
    return {
      label: `был вчера в ${at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`,
      state: 'away',
    };
  }

  return { label: `был ${whenShort(lastSeenIso, now)}`, state: 'away' };
}
